import { useEffect, useCallback, useState } from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router'
import { useTrackEvent } from '@retailer-platform/dashboard/analytics'
import { routes, URL_ADMIN_CUSTOMERS_AND_ORDERS_IPP_BASE } from '../../routing/routes'
import { useDomainAccessControl } from '../domain/accessControl'
import { CustomerSearchFastFindAccessControl } from '../../access-control/customersAndOrdersAdminAccess.configuration'
import { trackFastFindModalClosed, trackFastFindModalOpened } from '../domain/tracking'

const DOUBLE_KEY_WINDOW_MS = 500

export const useFastFind = () => {
  const history = useHistory()
  const location = useLocation()
  const trackEvent = useTrackEvent()
  const fastFindEnabled = useRouteMatch(URL_ADMIN_CUSTOMERS_AND_ORDERS_IPP_BASE)
  const hasAccess = useDomainAccessControl()
  const hasFastFindAccess = hasAccess(CustomerSearchFastFindAccessControl)
  const [isOpen, setIsOpen] = useState(false)
  const openModal = useCallback(() => {
    trackFastFindModalOpened(trackEvent, { sourcePage: location.pathname })
    setIsOpen(true)
  }, [trackEvent, location.pathname])
  const closeModal = useCallback(() => {
    trackFastFindModalClosed(trackEvent, { sourcePage: location.pathname })
    setIsOpen(false)
  }, [trackEvent, location.pathname])
  const dismissModal = useCallback(() => setIsOpen(false), [])

  const navigateToSearch = useCallback(
    (query: string) => {
      const trimmed = query.trim()
      if (!trimmed) return
      setIsOpen(false)
      history.push(
        `${routes['app-admin-customers-and-orders-ipp-customer-search']}?query=${encodeURIComponent(trimmed)}`
      )
    },
    [history]
  )

  useEffect(() => {
    if (!fastFindEnabled || !hasFastFindAccess) return

    let lastFKeyTime = 0

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      if (e.key === 'f' || e.key === 'F') {
        const now = Date.now()
        if (now - lastFKeyTime <= DOUBLE_KEY_WINDOW_MS) {
          e.preventDefault()
          openModal()
          lastFKeyTime = 0
        } else {
          lastFKeyTime = now
        }
      } else {
        lastFKeyTime = 0
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [openModal, fastFindEnabled, hasFastFindAccess])

  return { isOpen, openModal, closeModal, dismissModal, navigateToSearch }
}
